<template>
  <div data-testid="so-catalog-fiscal-page">
    <!--
      NOT a page-level filter widget (no filters-bar / filter-group /
      filter-actions class — see no-page-level-filter-widgets):
        - franchise-select is READ: which franchise's pendências we are
          looking at. franchise_id is a REQUIRED query param on both
          /missing and /get — this is not filtering an already-loaded list,
          it is the request itself.
        - scope-select is WRITE: it decides so_service_fiscal_franchise_id
          in the salvar() payload (null = global row, N = that franchise's
          override). It does not read from or duplicate the global filter.
    -->
    <div class="so-catalog-fiscal-controls">
      <franchise-select
        v-model="selectedFranchiseId"
        elem-id="so-catalog-fiscal-franquia"
        data-testid="so-catalog-fiscal-franchise-select"
        :label="t('Franquia (pendências)')"
        @franchise-data="handleFranchiseData"
      />
      <select-list
        v-model="scope"
        elem-id="so-catalog-fiscal-escopo"
        data-testid="so-catalog-fiscal-scope-select"
        :itens-select="scopeOptions"
        :label="t('Escopo de escrita')"
        :auto-select-first="false"
      />
    </div>

    <inline-message
      v-if="franchiseData && !isBrazil"
      severity="warn"
      data-testid="so-catalog-fiscal-country-unavailable"
    >
      {{ t('Dados fiscais brasileiros (LC116/NCM) não se aplicam a esta franquia — o trilho fiscal desta tela é específico do Brasil.') }}
    </inline-message>

    <tab-view v-else v-model="activeTab">
      <template #tablist>
        <TabList>
          <Tab value="services" data-testid="so-catalog-fiscal-tab-services">{{ t('Serviços') }}</Tab>
          <Tab value="additional_services" data-testid="so-catalog-fiscal-tab-additional-services">{{ t('Serviços adicionais') }}</Tab>
          <Tab value="products" data-testid="so-catalog-fiscal-tab-products">{{ t('Produtos') }}</Tab>
        </TabList>
      </template>
      <TabViewPanel value="services">
        <s-o-catalog-fiscal-services-table
          :franchise-id="selectedFranchiseIdNumber"
          :scope="scope"
        />
      </TabViewPanel>
      <TabViewPanel value="additional_services">
        <s-o-catalog-fiscal-additional-services-table
          :franchise-id="selectedFranchiseIdNumber"
          :scope="scope"
        />
      </TabViewPanel>
      <TabViewPanel value="products">
        <s-o-catalog-fiscal-products-table
          :franchise-id="selectedFranchiseIdNumber"
          :scope="scope"
        />
      </TabViewPanel>
    </tab-view>

    <!-- SOCatalogFiscalModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TabView from '../../ui/TabView.vue';
import TabViewPanel from '../../ui/TabViewPanel.vue';
import { permissionsService, type FranquiaData } from '../../services/permissionsService';
import type { FiscalScope } from '../../composables/useSOCatalogFiscal';
import SOCatalogFiscalServicesTable from './SOCatalogFiscalServicesTable.vue';
import SOCatalogFiscalProductsTable from './SOCatalogFiscalProductsTable.vue';
import SOCatalogFiscalAdditionalServicesTable from './SOCatalogFiscalAdditionalServicesTable.vue';

type Id = number | string;

const { t } = useI18n();

const selectedFranchiseId = ref<Id | undefined>(undefined);
const franchiseData = ref<FranquiaData | null>(null);
const scope = ref<FiscalScope>('franquia');
const activeTab = ref<'services' | 'products' | 'additional_services'>('services');

const selectedFranchiseIdNumber = computed<number | undefined>(() => {
  if (selectedFranchiseId.value == null || selectedFranchiseId.value === '') return undefined;
  const num = Number(selectedFranchiseId.value);
  return Number.isNaN(num) ? undefined : num;
});

const isFull = computed<boolean>(() => permissionsService.isUserInGroups(['Full']));

// Only Full users may write the global row — offering the option to anyone
// else would be offering a guaranteed error (service_fiscal.php::salvar()).
const scopeOptions = computed<{ ID: FiscalScope; Name: string }[]>(() => {
  const options: { ID: FiscalScope; Name: string }[] = [
    { ID: 'franquia', Name: t('Somente a franquia selecionada') },
  ];
  if (isFull.value) {
    options.unshift({ ID: 'global', Name: t('Global (franqueadora) — vale para todas as franquias') });
  }
  return options;
});

// Accept both spellings the `franquias.pais` column carries in practice
// ('Brasil' and the abbreviation 'BR') — a franchise on the abbreviated
// form must not be locked out of its own country's fiscal screen.
const isBrazil = computed<boolean>(() => {
  const pais = franchiseData.value?.Dados?.Pais;
  return pais === 'Brasil' || pais === 'BR';
});

const handleFranchiseData = (data: FranquiaData): void => {
  franchiseData.value = data;
};
</script>

<style scoped>
.so-catalog-fiscal-controls {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
</style>
